/* eslint-disable no-console */
/* eslint-disable no-alert */
import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import {
  sendVerificationCode,
  checkVerificationCode,
} from 'src/apis/utils/ticket/ticket';
import { TICKETING_ID } from 'src/assets/database/ticketData';
import TelConfirmedCancel from '../modal/TelConfirmedCancel';

export default function TicketCancelInputField({
  phoneNumber,
  setPhoneNumber,
  isVerified,
  setIsVerified,
  openModal,
  setStartFetch,
}: {
  phoneNumber: string;
  setPhoneNumber: (val: string) => void;
  isVerified: boolean;
  setIsVerified: (val: boolean) => void;
  openModal: (modalName: string) => void;
  setStartFetch: (val: boolean) => void;
}) {
  const [authCode, setAuthCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  // codeSent
  const [timeLeft, setTimeLeft] = useState(0); // 초 단위 (예: 180초 = 3분)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [timerActive, setTimerActive] = useState(false);
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);

  const handleAuthInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAuthCode(e.target.value);
  };

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '');
    setPhoneNumber(onlyNums);
  };

  const formatTime = (seconds: number) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleSendCode = async () => {
    try {
      await sendVerificationCode({ eventId: TICKETING_ID, phoneNumber });
      setCodeSent(true);
      setIsVerified(false);
      setTimeLeft(180); // 3분
      setTimerActive(true);
      openModal('CodeSent');
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = err as any;
      const errorCode = error?.response?.data?.code;
      if (errorCode === 'T001') {
        openModal('TicketSoldOut'); // 티켓 마감된 경우
      } else {
        openModal('CodeSendFailed'); // 그 외 전송 실패
      }
    }
  };

  const handleVerifyCode = async () => {
    try {
      const response = await checkVerificationCode({
        phoneNumber,
        code: authCode,
      });

      const { token } = response.data;
      sessionStorage.setItem('guestToken', token);

      setIsVerified(true);
      setTimerActive(false);
      setShowVerifiedModal(true);
    } catch (error) {
      console.error('인증 실패', error);
      openModal('NotConfirmed');
    }
  };

  useEffect(() => {
    if (timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(interval);
    };
  }, [timeLeft]);

  return (
    <>
      <InputWrapper>
        <InputBox>
          <TelNumberInput
            placeholder="전화번호 입력(-없이 입력)"
            type="tel"
            maxLength={11}
            value={phoneNumber}
            onChange={handlePhoneInputChange}
            disabled={isVerified}
            $isVerified={isVerified}
          />
          <TelAuthButton onClick={handleSendCode} disabled={isVerified}>
            인증요청
          </TelAuthButton>
        </InputBox>
        <InputBox>
          <AuthInputWrapper>
            <AuthNumberInput
              placeholder="인증번호 입력"
              type="tel" // number로 하면 maxLength가 안 먹음
              value={authCode}
              onChange={handleAuthInputChange}
              maxLength={6}
              disabled={isVerified || timeLeft <= 0}
              $isVerified={isVerified}
            />
            {codeSent && !isVerified && (
              <TimerText>
                {timeLeft > 0 ? formatTime(timeLeft) : '시간초과'}
              </TimerText>
            )}
          </AuthInputWrapper>

          <AuthNumberButton
            disabled={authCode.length < 4 || isVerified || timeLeft <= 0}
            onClick={handleVerifyCode}
          >
            확인
          </AuthNumberButton>
        </InputBox>
        {showVerifiedModal && (
          <TelConfirmedCancel
            onClose={() => {
              setShowVerifiedModal(false);
              setStartFetch(true);
            }}
          />
        )}
      </InputWrapper>
    </>
  );
}

const AuthInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const TimerText = styled.span`
  color: #f00;
  text-align: right;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.3px;

  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

const InputWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 48px;
`;
const InputBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin: 6px 0;
`;
const TelNumberInput = styled.input<{ $isVerified: boolean }>`
  padding: 14px 16px;

  height: 52px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid #0b2873;
  background: ${({ $isVerified }) => ($isVerified ? '#D1D1D1' : '#fff')};

  color: ${({ $isVerified }) => ($isVerified ? '#576589' : '#0b2873')};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: -0.4px;

  &::placeholder {
    color: #999;
  }
`;
const TelAuthButton = styled.button<{ disabled?: boolean }>`
  border-radius: 8px;
  background: ${({ disabled }) => (disabled ? '#B5BED9' : '#0b2873')};
  height: 52px;
  width: 87px;

  color: #fff;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
  letter-spacing: -0.4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  flex-shrink: 0;
`;

const AuthNumberInput = styled.input<{ $isVerified: boolean }>`
  padding: 14px 16px;

  height: 52px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid #0b2873;
  background: ${({ $isVerified }) => ($isVerified ? '#D1D1D1' : '#fff')};

  color: ${({ $isVerified }) => ($isVerified ? '#576589' : '#0b2873')};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: -0.4px;

  &::placeholder {
    color: #999;
  }
`;
const AuthNumberButton = styled.button`
  background: ${({ disabled }) => (disabled ? '#B5BED9' : '#0b2873')};

  border-radius: 8px;
  height: 52px;
  width: 60px;

  color: #fff;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
  letter-spacing: -0.4px;

  flex-shrink: 0;
`;
